import { useState, useEffect, useRef } from "react";
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from "react-redux";
import "../static/project-dashboard-styles.css";
import bvtLogo from "../static/bvt_logo_revised2.svg";
import viewpointButtonLogo from "../static/viewpointButtonLogoActive.svg";
import concurButtonLogo from "../static/concur_logo.svg";
import invoicePortalLogo from "../static/invoice.svg";
import sfdcLogo from "../static/sfdc-logo.png";
import { toast } from "react-hot-toast";
import {
	Grid,
	Card,
	CardContent,
	Typography,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	Box,
	Tooltip,
} from "@mui/material";

// COMPONENT IMPORTS
import HeaderComponent from "./HeaderComponent";
import ProgressTrackerTable from "./ProjectViewComponents/ProgressTrackerComponent";
import OverUnderSummaryChart from "./ProjectViewComponents/OverUnderComponent";
import PCOTypeSummary from "./ProjectViewComponents/PCOTypeSummaryComponent";
import PaymentsAndInvoicingTable from "./ProjectViewComponents/PaymentsAndInvoicingTableComponent";
import InfoIconButton from "./ProjectViewComponents/InfoIconButtonComponent";
import TablePopoutButton from "./ProjectViewComponents/TablePopoutIconComponent";
import BudgetVarianceChart from "./ProjectViewComponents/BudgetVarianceChartComponent";
import ConcurExpenseTracker from "./ProjectViewComponents/ConcurExpenseTracker";
import RevenueSpreadCard from "./ProjectViewComponents/RevenueSpreadCard";
import MasterContractInfoCard from "./ProjectViewComponents/MasterContractInformationCard";
import CostsVsBillingsChart from "./ProjectViewComponents/CostsAndBillingsComponent";
import BVTTablesComponent from "./ProjectViewComponents/BVTTablesComponent";
import QuickLinks from "./ProjectViewComponents/QuickLinksComponent";
import {
	toastStyle,
} from "../Containers/continerUtilityFunctions";
import ProjectTeamSection from './ProjectViewComponents/ProjectTeamSection';
import ProjectInformationSummarySection from './ProjectViewComponents/ProjectInformationSummarySection';
import ProjectMilestonesSection from './ProjectViewComponents/ProjectMilestonesSection';
import CFASplitSection from './ProjectViewComponents/CFASplitSection';
import ProjectLocationSection from './ProjectViewComponents/ProjectLocationSection';

import {
	fetchProjectData,
	fetchProjectSettings,
	fetchProjectInvoiceData,
	fetchProjectTeam,
	fetchProjectBudgetData,
	fetchAccessToProject,
	fetchRevenueSpread,
	fetchMonthlyCostStatusSubmissionData,
} from "../Containers/api";
import {
	setProjectData,
	setBudgetData,
	setTeamData,
	setSelectedProjectId,
	setSelectedProject,
	setRevenueSpreadData,
	setInvoiceData,
	setSettingsData,
	setMonthlyCostStatusData,
} from "../../../store/slices/projectSlice";
import MonthlyCostStatusSnapshotBox from "./ProjectViewComponents/MonthlyCostStatusSnapshotComponent";
import CombinedContingencyProfitChart from "./ProjectViewComponents/CombinedContingencyAndProfitChartComponent";

const classes = {};

const StyledContainer= styled(Box)(({ theme }) => ({
	padding: theme.spacing(.5),
}));

// Define Redux Selectors
const useReduxSelectors = () => {
	const projectData = useSelector((state) => state.project.projectData);
	const authenticated = useSelector((state) => state.global.authenticated);
	const isBeta = useSelector((state) => state.global.isBeta);
	const budgetData = useSelector((state) => state.project.budgetData);
	const teamData = useSelector((state) => state.project.teamData);
	const selectedProject = useSelector((state) => state.project.selectedProject);
	const selectedProjectId = useSelector(
		(state) => state.project.selectedProjectId
	);
	const revenueSpreadData = useSelector(
		(state) => state.project.revenueSpreadData
	);
	const invoiceData = useSelector((state) => state.project.invoiceData);
	const tab = useSelector((state) => state.global.tab);
	const settingsData = useSelector((state) => state.project.settingsData);

	return {
		projectData,
		authenticated,
		budgetData,
		teamData,
		selectedProjectId,
		selectedProject,
		revenueSpreadData,
		invoiceData,
		tab,
		isBeta,
		settingsData,
	};
};

const ProjectDashboard = ({}) => {

	// Redux - useDispatch and Destructure Redux Selectors
	const dispatch = useDispatch();
	const {
		authenticated,
		projectData,
		budgetData,
		teamData,
		selectedProjectId,
		selectedProject,
		revenueSpreadData,
		invoiceData,
		tab,
		isBeta,
		settingsData,
	} = useReduxSelectors();

	const [hasAccess, setHasAccess] = useState(null);
	const [accessPossible, setAccessPossible] = useState(null);
	const [accessChecked, setAccessChecked] = useState(false);
	const [noAccessDialogOpen, setNoAccessDialogOpen] = useState(false);
	const [sourceProcore, setSourceProcore] = useState("procore");
	const [selectedSnapshots, setSelectedSnapshots] = useState([]);
	const [initialSnapshots, setInitialSnapshots] = useState([]);

	const [firstSnapshot, setFirstSnapshot] = useState("");
	const [secondSnapshot, setSecondSnapshot] = useState("");
	// let selectedCompany = []

	const fullResetView = () => {
		dispatch(setSelectedProject(null));
		dispatch(setSelectedProjectId(null));
		dispatch(setRevenueSpreadData({}));
		dispatch(setProjectData({}));
		dispatch(setBudgetData([]));
		setSelectedSnapshots([]);
		setInitialSnapshots([]);
		dispatch(setInvoiceData([]));
		dispatch(setSettingsData([]));
		dispatch(setTeamData([]));
		setShowAllCombinedData(false);
	};

	// Call Function when we switch from projectView to portfolioView
	useEffect(() => {
		if (tab === "portfolio") {
			fullResetView();
		}
	}, [tab]);

	// Clears everything in Project View when selectedProjectID is set to null (no proj selected)
	useEffect(() => {
		if (selectedProjectId === null) {
		dispatch(setProjectData({}));
		dispatch(setBudgetData([]));
		setSelectedSnapshots([]);
		setInitialSnapshots([]);
		dispatch(setInvoiceData([]));
		dispatch(setSettingsData([]));
		dispatch(setTeamData([]));
		dispatch(setRevenueSpreadData({}));
		}
	}, [selectedProjectId]);

	// Refs for image button logic
	const bvtTableIconPopout = useRef(null);
	const concurTableIconPopout = useRef(null);
	const jobCostTrackerPopout = useRef(null);
	const invoicePortalPopout = useRef(null);

	const handleBVTLogoClick = () => {
		if (bvtTableIconPopout.current) {
		bvtTableIconPopout.current.triggerClick();
		}
	};

	const handleConcurLogoClick = () => {
		if (concurTableIconPopout.current) {
			concurTableIconPopout.current.triggerClick();
		}
	};

	const handleJobCostTrackerLogoClick = () => {
		if (jobCostTrackerPopout.current) {
			jobCostTrackerPopout.current.triggerClick();
		}
	};

	const handleInvoicePortalLogoClick = () => {
		if (invoicePortalPopout.current) {
			invoicePortalPopout.current.triggerClick();
		}
	};

	// Project Team -- View ALL Feature
	const roles = teamData || [];


	// Graph Logic
	const [showAllCombinedData, setShowAllCombinedData] = useState(false);

	useEffect(() => {
		if (
			!budgetData ||
			!budgetData.profit_data ||
			budgetData.profit_data.length === 0
		) return;

	}, [selectedProjectId, selectedProject]);

	// ============ ACCESS CHECK WHEN PROJECTID CHANGES =============
	// When selectedProjectId changes, we check access.
	useEffect(() => {
		if (!selectedProjectId || !authenticated) {
		// If no project selected, reset access states
		setHasAccess(null);
		setAccessPossible(null);
		setAccessChecked(false);
		return;
		}

		const fetchAccessToProjectAsync = async () => {
		try {
			const resData = await fetchAccessToProject(selectedProjectId);
			if (resData.access === true && resData.access_possible === true) {
			setHasAccess(true);
			setAccessPossible(true);
			toast.success("Access granted for this project!", toastStyle());
			} else {
			if (resData.access === false && resData.access_possible === true) {
				toast.error(
				"You do not have access to this project, but access is possible.",
				toastStyle()
				);
				setHasAccess(false);
				setAccessPossible(true);
				setNoAccessDialogOpen(true);
			} else if (
				resData.access === false &&
				resData.access_possible === false
			) {
				toast.error(
				"Access to this project is not possible given your current access priviledges.",
				toastStyle()
				);
				setNoAccessDialogOpen(true);
				setHasAccess(false);
				setAccessPossible(false);
			}
			}
			setAccessChecked(true);
		} catch (error) {
			console.error("Error checking user's access:", error);
			toast.error(error.message || "An unexpected error occurred.", toastStyle());
		}
		};
		fetchAccessToProjectAsync();
	}, [selectedProjectId]);

	const handleCloseNoAccessDialog = () => {
		setNoAccessDialogOpen(false);
	};

	// Setting Default snapshots for over/under details call
	useEffect(() => {
		if (selectedSnapshots && selectedSnapshots.length > 0) {
			if (!firstSnapshot && selectedSnapshots[0]) {
				setFirstSnapshot(selectedSnapshots[0]);
			}

			if (!secondSnapshot && selectedSnapshots.length > 1) {
				setSecondSnapshot(selectedSnapshots[selectedSnapshots.length - 1]);
			}
		}
	}, [selectedSnapshots, firstSnapshot, secondSnapshot]);

	// Combined Fetch for grabbing all initial data triggered off of projectID change
	// Combined Fetch for initial data
	useEffect(() => {
		if (!selectedProjectId || !accessChecked) return;
		if (hasAccess === false) {
			return; // Do not run any API calls
		}
		if (hasAccess === true) {
		if (selectedProjectId) {
			const fetchData = async () => {
			try {
				const projectData = await fetchProjectData(selectedProjectId);
				if (projectData) {
				dispatch(setProjectData(projectData));
				}

				// Update Settings
				const settingsData = await fetchProjectSettings(selectedProjectId);
				if (settingsData) {
					dispatch(setSettingsData(settingsData));

					// Update snapshots
					if (settingsData.snapshots) {
						// Keyed by the month each snapshot answers for, which is not always its
						// capture month; flattening by value alone loses that.
						const allSnapshots = Object.entries(settingsData.snapshots).flatMap(
							([yearMonth, monthSnapshots]) =>
								monthSnapshots.map((snapshot) => ({
									year_month: yearMonth,
									...snapshot,
								}))
						);
						setInitialSnapshots(allSnapshots);
						const initialSelectedSnapshots = allSnapshots.filter(
							(snapshot) => snapshot.selected
						);
						setSelectedSnapshots(initialSelectedSnapshots);
					}
				}

				// Handle Monthly Cost Status Data
				const monthlyCostStatusData = await fetchMonthlyCostStatusSubmissionData(selectedProjectId);
				dispatch(setMonthlyCostStatusData(monthlyCostStatusData || {}));

				// Handle Invoice Data
				const invoiceData = await fetchProjectInvoiceData(selectedProjectId);
				dispatch(setInvoiceData(invoiceData.data || []));

			} catch (error) {
				console.error("Error fetching project data:", error);
				toast.error(error.message || "An unexpected error occurred.", toastStyle());
				dispatch(setInvoiceData([]));
			}
			};

			// Clearing Previous Budget Data
			dispatch(setBudgetData([]));
			fetchData();
		}
		}
	}, [selectedProjectId, accessChecked, hasAccess]);

	// Fetch Team Data
	useEffect(() => {
		if (!selectedProjectId || !accessChecked || hasAccess === false) {
		return;
		}
		if (hasAccess === true) {
		const fetchTeamData = async () => {
			try {
			const teamData = await fetchProjectTeam(selectedProjectId);

			if (Array.isArray(teamData)) {
				dispatch(setTeamData(teamData));
			} else {
				console.error("Invalid team data format:", teamData);
			}
			} catch (error) {
			console.error("Error fetching team data:", error);
			toast.error(error.message || "An unexpected error occurred.", toastStyle());
			}
		};

		fetchTeamData();
		}
	}, [selectedProjectId, accessChecked, hasAccess]);

	// Fetch Revenue Spread Data
	useEffect(() => {
		if (!selectedProjectId || !accessChecked || hasAccess === false) {
		return;
		}
		if (hasAccess === true) {
		const fetchRevenueSpreadDataAsync = async () => {
			try {
			const revenueSpreadData = await fetchRevenueSpread(selectedProjectId);
			if (revenueSpreadData) {
				dispatch(setRevenueSpreadData(revenueSpreadData || []));
			}
			} catch (error) {
			console.error("Error fetching revenue spread data:", error);
			toast.error(error.message || "An unexpected error occurred.", toastStyle());
			}
		};
		fetchRevenueSpreadDataAsync();
		}
	}, [selectedProjectId, accessChecked, hasAccess]);

	// Separate useEffect for fetching budgetData whenever selectedSnapshots or selectedProjectId changes -- still getting called multiple times, but getting there
	// Budget Data Fetch
	useEffect(() => {
		if (!selectedProjectId || !accessChecked || hasAccess === false || selectedSnapshots.length === 0) {
			return;
		}

		if (hasAccess === true && selectedProjectId && selectedSnapshots.length > 0) {
		const fetchBudgetData = async () => {
			try {
			const data = await fetchProjectBudgetData(selectedProjectId);
			if (data) {
				dispatch(setBudgetData(data || []));
			} else {
				console.error(
				"Failed to fetch project budget data from HUB API:",
				data.message
				);
			}
			} catch (e) {
			console.error("Error fetching budget data:", e);
			toast.error(e.message || "An unexpected error occurred.", toastStyle());
			}
		};

		fetchBudgetData();
		}
	}, [
		selectedSnapshots,
		selectedProjectId,
		accessChecked,
		hasAccess,
		projectData,
		dispatch,
	]);

	// Render Project View
	const renderProjectView = () => (
		<Grid
			id="main-container-proj"
			container
			spacing={{ xs: 1, sm: 1.5, md: 2 }}
			sx={{
				mx: { md: -.5 },   // negating some sort of padding/margin from parent container here IDK - SL
			}}
		>
			{(tab === "project") && (
				<>
					{/* Column 1 */}
					<Grid size={{ xs: 12, md: 6, lg: 4 }}>
						<Grid
							container
							spacing={{ xs: 1, sm: 1.5 }}
							sx={{ width: "100%", justifyContent: "center" }}
						>
							<Grid
								size={12}
								sx={{ display: "flex", flexDirection: "column" }}
							>
								<Card className="card-content-container">
									<CardContent className="card-content card-content-mb10">
										<ProjectInformationSummarySection
											projectData={projectData}
											budgetData={budgetData}
											sourceProcore={sourceProcore}
										/>
									</CardContent>
								</Card>
							</Grid>
							<Grid
								size={12}
								sx={{ display: "flex", flexDirection: "column", height: "100%" }}
							>
								<Card
									className="card-content-container"
									sx={{ display: "flex", flexDirection: "column", flex: 1 }}
								>
								<CardContent className="card-content card-content-flex1">
									<ProjectMilestonesSection projectData={projectData} />
								</CardContent>
								</Card>
							</Grid>
							<Grid size={12}>
								<Card className="card-content-container">
								<CardContent className="card-content">
									<ProjectTeamSection
										selectedProject={selectedProject}
										projectData={projectData}
										teamData={teamData}
										tab={tab}
										roles={roles}
										classes={classes}
									/>
								</CardContent>
								</Card>
							</Grid>
							<Grid
								size={12}
								sx={{ display: "flex", flexDirection: "column" }}
							>
								<Card
								className="card-content-container"
								sx={{ display: "flex", flexDirection: "column", flex: 1 }}
								>
								<CardContent className="card-content">
									<CFASplitSection projectData={projectData} />
								</CardContent>
								</Card>
							</Grid>
							<Grid
								size={12}
								sx={{ display: "flex", flexDirection: "column", height: "100%" }}
							>
								<Card
								className="card-content-container"
								sx={{ display: "flex", flexDirection: "column", flex: 1 }}
								>
								<CardContent className="card-content" sx={{ flex: 1 }}>
									<ProjectLocationSection projectData={projectData} />
								</CardContent>
								</Card>
							</Grid>
						</Grid>
					</Grid>

					{/* Column 2 */}
					<Grid size={{ xs: 12, md: 6, lg: 4 }}>
						<Grid
							container
							spacing={{ xs: 1, sm: 1.5 }}
							sx={{ width: "100%", justifyContent: "center" }}
						>
						<Grid
							size={12}
							sx={{ display: "flex", flexDirection: "column" }}
						>
							<Card className="card-content-container">
							<CardContent className="card-content">
								<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "-15px",
									marginTop: "-15px",
								}}
								>
								<Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
									<Typography
										variant="subtitle1"
										className="card-title"
										sx={{ textAlign: "left", verticalAlign: "top" }}
									>
										Contract Financial Summary
									</Typography>
									{settingsData?.project_id === selectedProjectId &&
										settingsData?.other_meta_data?.phd_sync !== undefined && (
										<Tooltip
											title={
												settingsData.other_meta_data.phd_sync
													? "Salesforce Sync is enabled — Cost Status Profit and Revised Contract Amount sync to Salesforce on submission"
													: "Salesforce Sync is disabled — data is not syncing to Salesforce"
											}
											arrow
										>
											<div className={`sf-sync-badge ${settingsData.other_meta_data.phd_sync ? "sf-sync-enabled" : "sf-sync-disabled"}`}>
												{settingsData.other_meta_data.phd_sync ? (
													<>
														<img src={sfdcLogo} alt="Salesforce" className="sf-sync-logo" /> SF Sync
													</>
												) : (
													<>
														<img src={sfdcLogo} alt="Salesforce" className="sf-sync-logo sf-sync-logo-disabled" /> SF Sync Off
													</>
												)}
											</div>
										</Tooltip>
									)}
								</Box>
								<InfoIconButton
									projectData={projectData}
									source="contract-financial-summary"
								/>
								</Box>
								<MasterContractInfoCard
								projectData={projectData}
								budgetData={budgetData}
								/>
							</CardContent>
							</Card>
						</Grid>
						<Grid size={12}>
							<Card className="card-content-container" sx={{ display: "flex", flexDirection: "column", flex: 1 }}
							>
							<CardContent className="card-content">
								<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginLeft: "0px",
									marginRight: "0px",
									marginBottom: "0px",
									marginTop: "-12px",
									padding: "0px 15px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									sx={{ textAlign: "left", verticalAlign: "top" }}
								>
									Budget Variance
								</Typography>
								<Box
									sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
									justifyContent: "center",
									cursor: "pointer",
									}}
								>
									<TablePopoutButton
									ref={bvtTableIconPopout}
									budgetData={budgetData}
									tableComponent={BVTTablesComponent}
									title={"Budget Variance Data"}
									selectedProject={selectedProject}
									source="bvt-table"
									selectedSnapshots={selectedSnapshots}
									projectId={selectedProjectId}
									/>
									<InfoIconButton
									projectData={projectData}
									source="contract-financial-summary"
									/>
								</Box>
								</Box>
								{selectedProject && (
								<>
									<div style={{ textAlign: "center", marginTop: "-10px" }}>
									<BudgetVarianceChart budgetData={budgetData} />
									</div>
									{(() => {
									const { Budget, Cost } =
										budgetData?.current_contract_summary
										?.contract_structure || {};
									if (
										!Budget ||
										Object.keys(Budget).length === 0 ||
										!Cost ||
										Object.keys(Cost).length === 0
									) {
										return null;
									}
									return (
										<span
										style={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											justifyContent: "center",
											cursor: "pointer",
										}}
										onClick={handleBVTLogoClick}
										src={bvtLogo}
										>
										<Typography
											variant="subtitle1"
											className="card-title"
											style={{ textAlign: "center" }}
										>
											Expand to explore the budget variances for this
											project.
										</Typography>
										</span>
									);
									})()}
								</>
								)}
							</CardContent>
							</Card>
						</Grid>
						<Grid size={12}>
							<Card
							className="card-content-container"
							sx={{ display: "flex", flexDirection: "column", flex: 1 }}
							>
							<CardContent className="card-content" sx={{ flex: 1 }}>
								<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "0px",
									marginTop: "-12px",
									padding: "0px 15px",
								}}
								>
								<Typography variant="subtitle1" className="card-title">
									Profit & Contingency
								</Typography>
								{budgetData && budgetData.profit_data && (
									<div>
									<Button
										variant="contained"
										className="smaller-primary-button"
										onClick={() => setShowAllCombinedData(!showAllCombinedData)}
									>
										{showAllCombinedData
										? "Show Last 6 Months"
										: "Show All Data"}
									</Button>
									<InfoIconButton
										projectData={projectData}
										source="profitChart"
									/>
									</div>
								)}
								</div>
								<div
								style={{
									width: "100%",
									height: "300px",
									marginLeft: "10px",
									marginRight: "10px",
									marginTop: "10px",
									marginBottom: "-10px",
								}}
								>
								<CombinedContingencyProfitChart
									showAllData={showAllCombinedData}
								/>
								</div>
							</CardContent>
							</Card>
						</Grid>
						<Grid
							size={12}
							sx={{ display: "flex", flexDirection: "column", height: "100%" }}
						>
							<Card
							className="card-content-container"
							sx={{ display: "flex", flexDirection: "column", flex: 1 }}
							>
							<CardContent
								className="card-content"
								sx={{ marginBottom: "-20px" }}
							>
								<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginLeft: "20px",
									marginRight: "20px",
									marginBottom: "-15px",
									marginTop: "-15px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Top Budget Pickups/Busts
								</Typography>
								<div style={{ display: "flex", alignItems: "center" }}>
									<TablePopoutButton
										budgetData={budgetData}
										tableComponent={OverUnderSummaryChart}
										selectedSnapshots={selectedSnapshots}
										title={"Top Budget Pickups & Busts"}
										source="trade over/under"
										selectedProject={selectedProject}
										firstSnapshot={firstSnapshot}
										secondSnapshot={secondSnapshot}
										setFirstSnapshot={setFirstSnapshot}
										setSecondSnapshot={setSecondSnapshot}
									/>
									<InfoIconButton
										projectData={projectData}
										source="budget-pickups-busts"
									/>
								</div>
								</div>
								<OverUnderSummaryChart
									budgetData={budgetData}
									isModal={false}
									selectedSnapshots={selectedSnapshots}
								/>
							</CardContent>
							</Card>
						</Grid>
						<Grid size={12} sx={{ display: "none" }}>
							<Card className="card-content-container">
								<CardContent
									className="card-content"
									sx={{ marginLeft: "20px", marginRight: "20px" }}
								>
									<Typography
										variant="subtitle1"
										className="card-title"
										style={{ textAlign: "left", verticalAlign: "top" }}
									>
										PCO Type Summary (Work in Progress)
									</Typography>
									<PCOTypeSummary budgetData={budgetData} />
								</CardContent>
							</Card>
						</Grid>
						</Grid>
					</Grid>

					{/* Column 3 */}
					<Grid size={{ xs: 12, md: 12, lg: 4 }}>
						<Grid container spacing={{ xs: 1, sm: 1.5 }}>
						{/* QUICK LINKS -- TO FINISH */}
						<Grid size={12}>
							<Card className="card-content-container">
							<CardContent
								className="card-content"
								sx={{ maxHeight: "150px", marginBottom: "-45px" }}
							>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: "-15px",
										marginTop: "-15px",
										padding: "0px",
									}}
								>
									<Typography
										variant="subtitle1"
										className="card-title"
										style={{ textAlign: "left", verticalAlign: "top" }}
									>
										Project Quick Links
									</Typography>
								</div>
								<QuickLinks
									projectData={projectData}
									selectedProjectId={selectedProjectId}
								/>
							</CardContent>
							</Card>
						</Grid>
						<Grid size={12} style={{ position: "relative" }}>
							<Card className="card-content-container">
								<CardContent
								className="card-content"
								sx={{ maxHeight: "360px", marginBottom: "-20px" }}
								>
								<div
									style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "-15px",
									marginTop: "-15px",
									padding: "0px",
									}}
								>
									<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
									>
										Monthly Cost Status Report
									</Typography>
								</div>
								<MonthlyCostStatusSnapshotBox />
								</CardContent>
							</Card>					
						</Grid>
					<Grid size={{ xs: 12, sm: 4, md: 4 }}>
						<Card className="expenses-card-content-container">
							<CardContent className="card-content">
								<div
									style={{
										display: "none",
										justifyContent: "space-between",
										alignItems: "center",
										marginTop: "-15px",
									}}
								>
									<Typography
										variant="subtitle1"
										className="card-title"
										style={{ textAlign: "left", verticalAlign: "top" }}
									>
										JC Dashboard
									</Typography>
									<TablePopoutButton
										ref={jobCostTrackerPopout}
										tableComponent={ConcurExpenseTracker}
										concurUrl={projectData?.project_data?.job_cost_tracker_url}
										title={"Job Cost Dashboard"}
										selectedProject={selectedProject}
										projectData={projectData}
										disabled={!projectData || Object.keys(projectData).length === 0}
										onClick={(e) => {
											if (!projectData || Object.keys(projectData).length === 0) {
												e.preventDefault();
												return;
											}
										}}
									/>
								</div>
								{selectedProject && (
									<>
										<div style={{ textAlign: "center", padding: "3px 0" }}>
											<img
												src={viewpointButtonLogo}
												alt="Job Cost Tracker"
												className="expense-table-logo-button"
												onClick={handleJobCostTrackerLogoClick}
												disabled={!projectData || Object.keys(projectData).length === 0}
											/>
										</div>
										<Typography
											className="card-title-medium"
											style={{ textAlign: "center" }}
										>
											Viewpoint Expenses
										</Typography>
									</>
								)}
							</CardContent>
						</Card>
					</Grid>
					<Grid size={{ xs: 12, sm: 4, md: 4 }}>
						<Card className="expenses-card-content-container">
							<CardContent className="card-content">
								<div
								style={{
									display: "none",
									justifyContent: "space-between",
									alignItems: "center",
									marginTop: "-15px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Concur Dashboard
								</Typography>
								<TablePopoutButton
									ref={concurTableIconPopout}
									tableComponent={ConcurExpenseTracker}
									concurUrl={projectData?.project_data?.concur_expense_tracker_url}
									title={"Concur Dashboard"}
									selectedProject={selectedProject}
									projectData={projectData}
									disabled={!projectData || Object.keys(projectData).length === 0}
									onClick={(e) => {
									if (
										!projectData ||
										Object.keys(projectData).length === 0
									) {
										e.preventDefault();
										return;
									}
									}}
								/>
								</div>
								{selectedProject && (
									<>
										<div style={{ textAlign: "center", padding: "3px 0" }}>
										<img
											src={concurButtonLogo}
											alt="Concur Dashboard"
											className="expense-table-logo-button"
											onClick={handleConcurLogoClick}
											disabled={!projectData || Object.keys(projectData).length === 0}
										/>
										</div>
										<Typography
										className="card-title-medium"
										style={{ textAlign: "center" }}
										>
										Concur Expenses
										</Typography>
									</>
								)}
							</CardContent>
						</Card>
					</Grid>
					<Grid size={{ xs: 12, sm: 4, md: 4 }}>
						<Card className="expenses-card-content-container">
							<CardContent className="card-content">
								<div
								style={{
									display: "none",
									justifyContent: "space-between",
									alignItems: "center",
									marginTop: "-15px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Invoice Portal
								</Typography>
								<TablePopoutButton
									ref={invoicePortalPopout}
									tableComponent={ConcurExpenseTracker}
									concurUrl={'https://arcoap.azurewebsites.net/'}
									title={"Invoice Portal"}
									source="invoice-portal"
									selectedProject={selectedProject}
									projectData={projectData}
									disabled={!projectData || Object.keys(projectData).length === 0}
									onClick={(e) => {
									if (
										!projectData ||
										Object.keys(projectData).length === 0
									) {
										e.preventDefault();
										return;
									}
									}}
								/>
								</div>
								{selectedProject && (
									<>
										<div style={{ textAlign: "center", padding: "3px 0" }}>
										<img
											src={invoicePortalLogo}
											alt="Invoice Portal"
											className="expense-table-logo-button"
											onClick={handleInvoicePortalLogoClick}
											disabled={!projectData || Object.keys(projectData).length === 0}
										/>
										</div>
										<Typography
											className="card-title-medium"
											style={{ textAlign: "center" }}
										>
										AP Invoices
										</Typography>
									</>
								)}
							</CardContent>
						</Card>
					</Grid>
						<Grid size={12}>
							<Card className="card-content-container">
							<CardContent
								className="card-content"
								sx={{ maxHeight: "360px", marginBottom: "-45px" }}
							>
								<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "-15px",
									marginTop: "-15px",
									padding: "0px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Revenue Spread by Years
								</Typography>
								<InfoIconButton
									projectData={projectData}
									source="salesforce"
								/>
								</div>
								<RevenueSpreadCard revenueSpreadData={revenueSpreadData} />
							</CardContent>
							</Card>
						</Grid>
						<Grid size={12}>
							<Card className="card-content-container">
							<CardContent
								className="card-content"
								sx={{ maxHeight: "360px", marginBottom: "-45px" }}
							>
								<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "-15px",
									marginTop: "-15px",
									padding: "0px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Cost vs Billings
								</Typography>
								<InfoIconButton
									projectData={projectData}
									source="costs-and-billings"
								/>
								</div>
								<CostsVsBillingsChart
									budgetData={budgetData}
									invoiceData={invoiceData}
								/>
							</CardContent>
							</Card>
						</Grid>
						<Grid size={12} style={{ position: "relative" }}>
							<Card
							className="card-content-container"
							// style={{ opacity: 0.5, pointerEvents: "none" }}
							>
							<CardContent
								className="card-content"
								sx={{ marginBottom: "-15px" }}
							>
								<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "-15px",
									marginTop: "-15px",
									paddingLeft: "20px",
									paddingRight: "20px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Payment & Invoicing History
								</Typography>
								<TablePopoutButton
									budgetData={budgetData}
									tableComponent={PaymentsAndInvoicingTable}
									title={"Payment & Invoicing History"}
									invoiceData={invoiceData}
									selectedProject={selectedProject}
									source="payment-invoicing"
								/>
								</div>
								<PaymentsAndInvoicingTable
								isModal={false}
								invoiceData={invoiceData}
								selectedProject={selectedProject}
								/>
							</CardContent>
							</Card>
						</Grid>
						
						<Grid size={12} style={{ display: "none" }}>
							<Card className="card-content-container">
							<CardContent className="card-content">
								<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginLeft: "20px",
									marginRight: "20px",
								}}
								>
								<Typography
									variant="subtitle1"
									className="card-title"
									style={{ textAlign: "left", verticalAlign: "top" }}
								>
									Progress Tracker (Work in Progress)
								</Typography>
								<TablePopoutButton
									budgetData={budgetData}
									tableComponent={ProgressTrackerTable}
									title={"Progress Tracker"}
								/>
								</div>
								<ProgressTrackerTable
								style={{ maxHeight: "150px" }}
								isModal={false}
								/>
							</CardContent>
							</Card>
						</Grid>
						</Grid>
					</Grid>
					{projectData?.project_data?.project_number && (
						<Grid size={12}>
							<Card className="card-content-container">
								<CardContent className="card-content">
									<Typography
										variant="subtitle1"
										className="card-title"
										style={{ textAlign: "left", verticalAlign: "top", marginLeft: "20px", marginBottom: "10px" }}
									>
										Project Operations
									</Typography>
									<iframe
										src={`https://app.powerbi.com/reportEmbed?reportId=f897a625-af27-4c1b-9a5c-1898da0286ff&autoAuth=true&ctid=db0fc84b-8300-4d5a-9d18-00141c8513e2&filter=Project%2Fproject_number+eq+%27${projectData?.project_data?.project_number}%27`}
										style={{ width: "100%", height: "90vh", border: "none" }}
										title="Project Operations"
										allowFullScreen
									/>
								</CardContent>
							</Card>
						</Grid>
					)}
				</>
			)}
		</Grid>
	);

	return (
        <StyledContainer
			style={{
				marginTop: "10px",
				height: "calc(100vh - 10px)",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Grid size={12} sx={{ display: "flex", flexDirection: "column", flex: "1 1 0", minHeight: 0 }}>
				<Box sx={{ flex: "0 0 auto" }}>
					<HeaderComponent
						projectData={projectData}
						selectedSnapshots={selectedSnapshots}
						setSelectedSnapshots={setSelectedSnapshots}
						selectedProjectId={selectedProjectId}
						initialSnapshots={initialSnapshots}
						setTeamData={setTeamData}
						setBudgetData={setBudgetData}
						setProjectData={setProjectData}
					/>
				</Box>
				{/* Conditionally Render Main Content (scrollable area) */}
				<Box sx={{ flex: "1 1 0", minHeight: 0, overflow: "auto" }}>
					{renderProjectView()}
				</Box>
			</Grid>
            {/* Access Dialog */}
            <Dialog open={noAccessDialogOpen} onClose={handleCloseNoAccessDialog}>
				<DialogTitle id="no-access-dialog-title">Access Issue</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{accessPossible === false
						? "Access to this project is not possible due to your current permissions."
						: "You do not have permission to view this project, but access is possible. Please make sure you are added to the Project Roles in the Procore project and then reload this app."}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseNoAccessDialog} color="primary">
						Close
					</Button>
				</DialogActions>
			</Dialog>
        </StyledContainer>
    );
	};

export default ProjectDashboard;